import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fetchPatronMatches,
  patronMembershipNumber,
  patronSearchKey,
  patronSearchLabel,
  PATRON_SEARCH_DEBOUNCE_MS,
  PATRON_SEARCH_MIN_CHARS,
} from '../../../utils/patronProfileSearch'

export function selectedPatronPayload(p) {
  if (!p) return { patron_name: null, member_id: null, master_profile_id: null }
  return {
    patron_name: patronSearchLabel(p),
    member_id: patronMembershipNumber(p) || null,
    master_profile_id: p.MasterProfileID || null,
  }
}

export default function PatronTypeahead({ value, onChange, inputStyle }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const seqRef = useRef(0)
  const debounceRef = useRef(null)
  const blurRef = useRef(null)

  const runSearch = useCallback(async (q) => {
    const seq = ++seqRef.current
    setSearching(true)
    setError('')
    try {
      const rows = await fetchPatronMatches(q)
      if (seq !== seqRef.current) return
      setResults(rows)
      setError(rows.length === 0 ? 'No patrons found — try name or membership number.' : '')
      setOpen(true)
    } catch (e) {
      if (seq !== seqRef.current) return
      setResults([])
      setError(e.message || 'Search unavailable.')
      setOpen(true)
    } finally {
      if (seq === seqRef.current) setSearching(false)
    }
  }, [])

  useEffect(() => {
    if (value) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const q = query.trim()
    if (q.length < PATRON_SEARCH_MIN_CHARS) {
      setResults([])
      setError('')
      setSearching(false)
      return
    }
    setSearching(true)
    debounceRef.current = setTimeout(() => runSearch(q), PATRON_SEARCH_DEBOUNCE_MS)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query, value, runSearch])

  const select = (p) => {
    if (blurRef.current) clearTimeout(blurRef.current)
    onChange(p)
    setQuery(patronSearchLabel(p))
    setResults([])
    setOpen(false)
    setError('')
  }

  const clear = () => {
    onChange(null)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  if (value) {
    const mn = patronMembershipNumber(value)
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        border: '1px solid #bbf7d0', background: '#f0fdf4',
        borderRadius: 8, padding: '8px 10px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#14532d' }}>{patronSearchLabel(value)}</div>
          <div style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#166534' }}>
            {mn ? `Membership ${mn}` : value.MasterProfileID || '—'}
          </div>
        </div>
        <button type="button" onClick={clear} style={{
          border: 0, background: 'none', color: '#166534', fontWeight: 700, fontSize: 12, cursor: 'pointer',
        }}>
          Change
        </button>
      </div>
    )
  }

  const showList = open && query.trim().length >= PATRON_SEARCH_MIN_CHARS

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          if (e.target.value.trim().length >= PATRON_SEARCH_MIN_CHARS) setOpen(true)
          else { setOpen(false); setResults([]); setError('') }
        }}
        onFocus={() => { if (query.trim().length >= PATRON_SEARCH_MIN_CHARS) setOpen(true) }}
        onBlur={() => { blurRef.current = setTimeout(() => setOpen(false), 160) }}
        placeholder="Name or membership number"
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls="co-run-patron-list"
        style={inputStyle}
      />
      {showList && (
        <ul
          id="co-run-patron-list"
          role="listbox"
          style={{
            position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%',
            margin: '4px 0 0', padding: 0, listStyle: 'none',
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
            maxHeight: 200, overflow: 'auto', boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
          }}
        >
          {searching && results.length === 0 && (
            <li style={{ padding: '8px 10px', fontSize: 13, color: '#64748b' }}>Searching…</li>
          )}
          {!searching && results.length === 0 && (
            <li style={{ padding: '8px 10px', fontSize: 13, color: '#92400e' }}>{error || 'No patrons found'}</li>
          )}
          {results.map((p) => {
            const mn = patronMembershipNumber(p)
            return (
              <li key={patronSearchKey(p)} role="option">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(p)}
                  style={{
                    width: '100%', textAlign: 'left', border: 0, background: '#fff',
                    padding: '8px 10px', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0a5169' }}>{patronSearchLabel(p)}</span>
                  <span style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#64748b', marginLeft: 8 }}>
                    {mn || 'No membership number'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
